import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, FileText, Download, ArrowRight, ShieldCheck, ScrollText, AlertTriangle, Lock, Headset } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { legal, LEGAL_CATEGORIES, LEGAL_META } from "../i18n/legal";
import { formatLegalDate } from "../i18n/legalUtils";
import { Reveal, Overline } from "../components/site/Reveal";

const CAT_ICONS = {
  contracts: ScrollText,
  risk: AlertTriangle,
  compliance: ShieldCheck,
  privacy: Lock,
  support: Headset,
};

export default function LegalLibrary() {
  const { lang } = useLang();
  const L = legal[lang];
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("all");

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return LEGAL_CATEGORIES.map((cat) => {
      const docs = cat.docs.filter((slug) => {
        if (activeCat !== "all" && activeCat !== cat.id) return false;
        const d = L.docs[slug];
        if (!d) return false;
        if (!q) return true;
        return (d.title + " " + (d.desc || "")).toLowerCase().includes(q);
      });
      return { ...cat, docs };
    }).filter((cat) => cat.docs.length > 0);
  }, [query, activeCat, L]);

  const total = groups.reduce((n, c) => n + c.docs.length, 0);

  return (
    <div data-testid="legal-library" className="bg-white min-h-screen">
      {/* Hero */}
      <section className="relative bg-slate-900 pt-[136px] pb-16 lg:pb-20 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <span className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.28em] font-mono font-medium text-emerald-400">
            <span className="h-px w-8 bg-emerald-500" />
            {L.ui.overline}
          </span>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05]"
            data-testid="legal-library-title"
          >
            {L.ui.libraryTitle}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed max-w-3xl"
          >
            {L.ui.librarySubtitle}
          </motion.p>
        </div>
      </section>

      {/* Controls */}
      <section className="border-b border-slate-200 bg-[#FAF9F6] sticky top-[72px] z-30 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-5">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="legal-search-input"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={L.ui.searchPlaceholder}
                className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-full text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 transition-colors duration-300"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                data-testid="legal-filter-all"
                onClick={() => setActiveCat("all")}
                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-[0.12em] transition-colors duration-300 border ${
                  activeCat === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
              >
                {L.ui.allCategories}
              </button>
              {LEGAL_CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  data-testid={`legal-filter-${c.id}`}
                  onClick={() => setActiveCat(c.id)}
                  className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-[0.12em] transition-colors duration-300 border ${
                    activeCat === c.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                  }`}
                >
                  {L.categories[c.id]}
                </button>
              ))}
            </div>
          </div>
          <p className="mt-3 text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500">
            {total} {L.ui.docsCount}
          </p>
        </div>
      </section>

      {/* Document groups */}
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        {total === 0 && (
          <p data-testid="legal-no-results" className="text-slate-500 text-sm py-10">
            {L.ui.noResults}
          </p>
        )}

        {groups.map((cat, gi) => {
          const Icon = CAT_ICONS[cat.id] || FileText;
          return (
            <section key={cat.id} data-testid={`legal-category-${cat.id}`} className="mb-16 last:mb-0">
              <Reveal>
                <div className="flex items-center gap-3 pb-5 border-b border-slate-200">
                  <Icon size={18} strokeWidth={1.6} className="text-emerald-700" />
                  <h2 className="font-serif text-xl md:text-2xl font-semibold text-slate-900 tracking-tight">{L.categories[cat.id]}</h2>
                  <span className="text-[11px] font-mono text-slate-400">{cat.docs.length}</span>
                </div>
              </Reveal>

              <div className="mt-7 grid md:grid-cols-2 gap-5">
                {cat.docs.map((slug, i) => {
                  const d = L.docs[slug];
                  const meta = LEGAL_META[slug];
                  return (
                    <Reveal key={slug} delay={0.03 * (i % 4)}>
                      <div
                        data-testid={`legal-card-${slug}`}
                        className="group h-full flex flex-col border border-slate-200 bg-white p-6 lg:p-7 hover:border-emerald-600/60 hover:shadow-[0_12px_40px_-24px_rgba(15,23,42,0.35)] transition-all duration-400"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <h3 className="font-serif text-lg font-semibold text-slate-900 leading-snug">{d.title}</h3>
                          <FileText size={18} strokeWidth={1.4} className="text-slate-300 shrink-0 mt-1 group-hover:text-emerald-600 transition-colors duration-300" />
                        </div>
                        <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{d.desc}</p>

                        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] font-mono uppercase tracking-[0.12em] text-slate-500">
                          <span>{L.ui.version} {meta.version}</span>
                          <span>{L.ui.effective} {formatLegalDate(meta.effective, lang)}</span>
                          <span className={meta.pdf ? "text-emerald-700" : "text-slate-400"}>{meta.pdf ? L.ui.pdfYes : L.ui.pdfNo}</span>
                        </div>

                        <div className="mt-6 pt-5 border-t border-slate-100 flex items-center gap-5">
                          <Link
                            data-testid={`legal-card-view-${slug}`}
                            to={`/legal/${slug}`}
                            className="inline-flex items-center gap-1.5 text-sm text-slate-900 hover:text-emerald-700 transition-colors duration-300"
                          >
                            {L.ui.view}
                            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
                          </Link>
                          {meta.pdf && (
                            <Link
                              data-testid={`legal-card-download-${slug}`}
                              to={`/legal/${slug}?print=1`}
                              className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700 transition-colors duration-300"
                            >
                              <Download size={14} />
                              {L.ui.download}
                            </Link>
                          )}
                        </div>
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            </section>
          );
        })}

        <Reveal>
          <div data-testid="legal-library-contact" className="mt-6 border border-slate-200 bg-[#FAF9F6] p-8 lg:p-10">
            <Overline>{L.ui.contactTitle}</Overline>
            <p className="mt-4 text-sm text-slate-600 leading-relaxed max-w-2xl">{L.ui.contactBody}</p>
            <p className="mt-6 text-xs text-slate-400 leading-relaxed max-w-3xl">{L.ui.placeholderNote}</p>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
